import {
  Avatar,
  Box,
  Button,
  Table,
  Text,
  useUnsafeMemo,
} from '@factorialco/deprecated-design-system'
import {
  BankAccountMembershipMembersDocument,
  BankingBankAccountFragment,
  RemoveBankAccountMembershipMemberDocument,
} from 'generated/resources/graphql'

import Popover from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

interface Props {
  bankAccount: BankingBankAccountFragment
  notRemovableEmployeeIds?: number[]
  onAddClick: () => void
}

export const SidepanelMemberships = ({
  bankAccount,
  notRemovableEmployeeIds,
  onAddClick,
}: Props) => {
  const { company } = useCurrentSession()

  const [removeMembership, { loading }] = useMutation(RemoveBankAccountMembershipMemberDocument, {
    refetchQueries: [BankAccountMembershipMembersDocument],
    extractorFn: (data) => data.bankingMutations.upsertBankingBankAccountMembership,
  })

  const { data } = useQuery(BankAccountMembershipMembersDocument, {
    variables: {
      bankAccountId: bankAccount.id,
    },
  })

  const members = useUnsafeMemo(() => {
    if (!data?.banking.bankAccountMemberships) return []

    const memberMap = new Map()

    data.banking.bankAccountMemberships.forEach((membership) => {
      membership.members?.forEach((member) => {
        memberMap.set(member.id, member)
      })
    })

    return Array.from(memberMap.values())
  }, [data?.banking.bankAccountMemberships])

  return (
    <Box gap={'s4'}>
      <Box flexDirection="row" justifyContent="spaceBetween" width="full" paddingBottom={'s8'}>
        <Box flexDirection="column" gap={'s4'}>
          <Text.Normal color="grey1000" weight="medium">
            {i18n.t('banking.accounts.finish_wizard.step_3.title')}
          </Text.Normal>
          <Text color="grey800" weight="normal" size="200">
            {i18n.t('banking.accounts.finish_wizard.step_3.subtitle')}
          </Text>
        </Box>
        <Box flexDirection="row" justifyContent="flexEnd" alignItems="center">
          <Button disabled={loading} type="primary" iconName="add" onClick={onAddClick}>
            {i18n.t('banking.accounts.side_panel.add_membership')}
          </Button>
        </Box>
      </Box>
      <Box maxHeight={'s240'} overflowY="auto">
        {bankAccount.bankAccountMembership?.id && (
          <Table autoLayout>
            <Table.Head>
              <Table.Row>
                <Table.HeadCell colSpan={9}>
                  {i18n.t('billing.subscription.plans.expenses.columns.user')}
                </Table.HeadCell>
                <Table.HeadCell colSpan={2}>
                  {i18n.t('billing.subscription.plans.expenses.columns.added_on')}
                </Table.HeadCell>
                <Table.HeadCell colSpan={1}></Table.HeadCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {members?.map((member) => (
                <Table.Row key={`member-${member.id}`}>
                  <Table.Cell colSpan={9} paddingX="s4">
                    <Box
                      flexDirection="row"
                      alignItems="center"
                      gap="s16"
                      minWidth="s96"
                      paddingLeft={'s16'}
                    >
                      <Avatar
                        size="s40"
                        url={buildUrl(member.access?.employee?.avatar?.url)}
                        label={member.access?.employee?.fullName || ''}
                      />
                      <Text.Normal size="200" weight="medium">
                        {member.access?.employee?.fullName || ''}
                      </Text.Normal>
                    </Box>
                  </Table.Cell>
                  <Table.Cell colSpan={2} paddingX="s4">
                    <Text.Normal size="200" weight="medium">
                      {member.createdAt
                        ? dateToString(stringToDate(member.createdAt, 'backend_datetime'), 'ui')
                        : ''}
                    </Text.Normal>
                  </Table.Cell>
                  <Table.Cell colSpan={1} paddingX="s4">
                    {notRemovableEmployeeIds &&
                      !notRemovableEmployeeIds.includes(member.access?.employee?.id) && (
                        <Box
                          flexDirection="row"
                          gap="s8"
                          justifyContent="flexEnd"
                          paddingRight={'s16'}
                        >
                          <Popover type="list">
                            <Popover.Trigger>
                              <Button.Decorative type="secondary" iconName="ellipsis" />
                            </Popover.Trigger>
                            <Popover.Content side="bottom" align="end">
                              <Popover.TouchableOption
                                onClick={() =>
                                  removeMembership({
                                    variables: {
                                      employeeIds: [member.access?.employee?.id],
                                      companyId: company.id,
                                      bankAccountId: bankAccount.id,
                                    },
                                  })
                                }
                                title={i18n.t('banking.accounts.side_panel.remove_membership')}
                                danger
                                disabled={loading}
                              />
                            </Popover.Content>
                          </Popover>
                        </Box>
                      )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Box>
    </Box>
  )
}
